import { Controller } from "stimulus"

// Handles editor sidebar state:
// - the sidebar element's own classes are the single shared open/closed state
// - the header reopen control and both edge tabs derive their visibility from it
// - Auto-closes sidebar when resizing below the configured breakpoint
// - Auto-reopens sidebar when resizing back above it (if it was open before)
export default class extends Controller {
  static targets = ["focusButton"]
  static values = { preferenceKey: String, breakpoint: Number }

  connect() {
    this.focusMode = false
    this._onEscape = event => {
      if (event.key !== "Escape" || event.defaultPrevented || !this.focusMode) return
      if (document.querySelector(".modal.show, dialog[open], .find-replace-bar")) return
      this.exitFocus()
    }
    this._beforeCache = () => this.setFocusMode(false, false)
    this._closePanel = () => this.closeSidebar()
    this._openPanel = () => this.openSidebar()
    this._toggleFocus = () => this.toggleFocus()
    // Remember where keyboard focus last was: compact layouts hide the focus-mode controls during
    // layout, which drops focus to <body> before any resize handler can read it.
    this._onFocusIn = event => { this.lastFocused = event.target }
    document.addEventListener("focusin", this._onFocusIn)
    this.element.addEventListener("editor:close-panel", this._closePanel)
    this.element.addEventListener("editor:open-panel", this._openPanel)
    this.element.addEventListener("editor:toggle-focus", this._toggleFocus)
    document.addEventListener("keydown", this._onEscape)
    document.addEventListener("turbo:before-cache", this._beforeCache)
    try { this.setFocusMode(localStorage.getItem(this.preferenceKeyValue) === "true", false) } catch (_) {}

    this.breakpoint = this.breakpointValue || 1024
    this.sidebar = document.getElementById("editorRightColumn")
    this.openButton = document.getElementById("editorSidebarOpenButton")
    this.wasAboveBreakpoint = window.innerWidth >= this.breakpoint
    this.wasOpenBeforeAutoClose = false

    // Check if sidebar is open on page load
    const isExpanded = this.sidebar?.classList.contains("sidebar-expanded") ||
                       this.sidebar?.classList.contains("d-lg-flex")

    // Auto-hide sidebar if page loads below breakpoint
    if (window.innerWidth < this.breakpoint && isExpanded) {
      this.wasOpenBeforeAutoClose = true
      this.closeSidebar()
    }

    this.handleResize = this.handleResize.bind(this)
    window.addEventListener("resize", this.handleResize)

    // The header control, AI chat helpers, backdrop and edge tabs only change the sidebar's
    // classes, so derive every control from that one state instead of caching local copies.
    this.controlsObserver = new MutationObserver(() => this.updateControls())
    if (this.sidebar) this.controlsObserver.observe(this.sidebar, { attributes: true, attributeFilter: ["class"] })
    if (this.sidebar && typeof ResizeObserver === "function") {
      this.sizeObserver = new ResizeObserver(() => this.positionCollapseTab())
      this.sizeObserver.observe(this.sidebar)
    }
    this.updateControls()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    document.removeEventListener("focusin", this._onFocusIn)
    document.removeEventListener("keydown", this._onEscape)
    document.removeEventListener("turbo:before-cache", this._beforeCache)
    this.element.removeEventListener("editor:close-panel", this._closePanel)
    this.element.removeEventListener("editor:open-panel", this._openPanel)
    this.element.removeEventListener("editor:toggle-focus", this._toggleFocus)
    this.controlsObserver?.disconnect()
    this.sizeObserver?.disconnect()
    this.setFocusMode(false, false)
  }

  toggleFocus() {
    this.setFocusMode(!this.focusMode)
  }

  // Single exit path shared by Escape and the visible header indicator, so neither
  // control duplicates the focus-mode state.
  exitFocus() {
    if (!this.focusMode) return
    this.setFocusMode(false)
    this.focusReturnTarget()?.focus({ preventScroll: true })
  }

  focusReturnTarget() {
    const tools = this.element.querySelector('.editor-appearance-toggle')
    return tools || (this.hasFocusButtonTarget ? this.focusButtonTarget : null)
  }

  setFocusMode(enabled, remember = true) {
    // Compact layouts have no Focus action; do not strand a remembered desktop mode.
    if (innerWidth < 1400 && this.element.querySelector('.editor-appearance-toggle')) enabled = false
    const content = this.element.querySelector(".editor-content")
    const anchor = this.readingAnchor(content)
    const top = anchor?.getBoundingClientRect().top
    this.focusMode = enabled
    document.body.classList.toggle("editor-focus-mode", enabled)
    if (this.hasFocusButtonTarget) {
      this.focusButtonTarget.innerHTML = `<span class="far fa-${enabled ? "compress" : "expand"}" aria-hidden="true"></span><span>${enabled ? "Exit focus" : "Focus"}</span>`
      this.focusButtonTarget.setAttribute("aria-pressed", String(enabled))
      this.focusButtonTarget.setAttribute("aria-label", enabled ? "Exit focus" : "Focus")
      this.focusButtonTarget.title = enabled ? "Exit focus (Esc)" : "Hide navigation and side panel"
    }
    // Only presentation changes: editor instances, selections and panel state stay intact.
    if (anchor && content.contains(anchor)) {
      const delta = anchor.getBoundingClientRect().top - top
      if (content.scrollHeight > content.clientHeight) content.scrollTop += delta
      else window.scrollBy(0, delta)
    }
    this.updateControls()
    this.element.dispatchEvent(new CustomEvent("editor:focus-changed"))
    if (remember && this.hasPreferenceKeyValue) {
      try { localStorage.setItem(this.preferenceKeyValue, String(enabled)) } catch (_) {}
    }
  }

  readingAnchor(content) {
    if (!content) return null
    const rect = content.getBoundingClientRect()
    const toolbar = content.querySelector(".tiptap-fixed-toolbar")?.getBoundingClientRect()
    const top = Math.max(rect.top, toolbar?.bottom || 0, 0) + 8
    const bottom = Math.min(rect.bottom, window.innerHeight)
    let partial = null
    for (const block of content.querySelectorAll(".ProseMirror > *")) {
      const bounds = block.getBoundingClientRect()
      if (bounds.top >= bottom) break
      if (bounds.top >= top) return block
      if (bounds.bottom > top) partial = block
    }
    return partial
  }

  handleResize() {
    const compactTools = innerWidth < 1400 && this.element.querySelector('.editor-appearance-toggle')
    const indicator = this.element.querySelector('.editor-focus-indicator')
    const isFocusControl = node => !!node && ((this.hasFocusButtonTarget && node === this.focusButtonTarget) || node === indicator)
    // Compact layouts hide every focus-mode control, and the browser drops focus to <body> as soon
    // as it does. Either reading (focus still on the control, or already handed to <body> from that
    // control) means the keyboard position must move to a control the writer can still use.
    const lostFromFocusControl = isFocusControl(document.activeElement)
      || (document.activeElement === document.body && this.focusMode && isFocusControl(this.lastFocused))
    if (compactTools && this.focusMode) this.setFocusMode(false, false)
    if (compactTools && lostFromFocusControl) compactTools.focus({ preventScroll: true })
    if (!this.sidebar) return

    const isAboveBreakpoint = window.innerWidth >= this.breakpoint
    const isExpanded = this.sidebar.classList.contains("sidebar-expanded")

    // Close when crossing FROM above TO below breakpoint
    if (this.wasAboveBreakpoint && !isAboveBreakpoint && isExpanded) {
      this.wasOpenBeforeAutoClose = true
      this.closeSidebar()
    }

    // Reopen when crossing FROM below TO above breakpoint (if it was open before)
    if (!this.wasAboveBreakpoint && isAboveBreakpoint && this.wasOpenBeforeAutoClose) {
      this.openSidebar()
      this.wasOpenBeforeAutoClose = false
    }

    this.wasAboveBreakpoint = isAboveBreakpoint
    this.updateControls()
  }

  // The sidebar element's own classes are the shared state: every open/close path writes those
  // classes and every control reads its visibility from them here.
  sidebarOpen() {
    if (!this.sidebar || this.sidebar.classList.contains("d-none")) return false
    // The panel is flagged expanded even while it is hidden (focus mode, preloader) or narrow.
    if (this.sidebar.classList.contains("sidebar-expanded")) return true
    return this.sidebar.getBoundingClientRect().width > 0
  }

  updateControls() {
    if (!this.sidebar) return
    const open = this.sidebarOpen()
    this.openButton = this.control("editorSidebarOpenButton") || this.openButton
    this.setControlVisible(this.openButton, !open)
    this.setControlVisible(this.control("panel-open-arrow"), !open)
    this.setControlVisible(this.control("panel-collapse-arrow"), open)
    if (open) this.positionCollapseTab()
  }

  control(id) {
    return document.getElementById(id)
  }

  setControlVisible(control, visible) {
    if (!control || control.classList.contains("d-none") === !visible) return
    control.classList.toggle("d-none", !visible)
  }

  positionCollapseTab() {
    const tab = this.control("panel-collapse-arrow")
    const rect = this.sidebar?.getBoundingClientRect()
    if (!tab || !rect || tab.classList.contains("d-none")) return
    if (rect.width === 0 || rect.left <= 0) return
    tab.style.left = `${rect.left - 24}px`
  }

  closeSidebar() {
    if (!this.sidebar) return
    const restoreFocus = this.sidebar.contains(document.activeElement)

    // Remove expanded classes
    this.sidebar.classList.remove("sidebar-expanded", "d-flex", "d-lg-flex")

    // Hide sidebar
    this.sidebar.classList.add("d-none")

    this.updateControls()

    if (restoreFocus && this.openButton) this.openButton.focus({ preventScroll: true })
  }

  openSidebar() {
    if (!this.sidebar) return

    // Remove hidden class
    this.sidebar.classList.remove("d-none")

    // Add expanded classes
    this.sidebar.classList.add("sidebar-expanded", "d-flex")

    this.updateControls()
  }

  // The edge tab closes the panel and returns it to the chat view, as the previous arrow did.
  collapseFromEdge() {
    document.getElementById("outline-panel-container")?.classList.add("d-none")
    document.getElementById("comments-panel-container")?.classList.add("d-none")
    document.getElementById("currentSidebarTheme")?.classList.remove("d-none")
    this.closeSidebar()
  }
}
